<template>
    <PageLoading v-if="isFetchingOrder" />
    <div v-else class="q-py-md profile-single-order">
        <!-- header row -->
        <div class="row q-col-gutter-x-lg items-center justify-end">
            <!-- back to orders list button -->
            <div class="col">
                <q-btn
                    class="btn-custom"
                    color="secondary"
                    flat
                    dense
                    :ripple="false"
                    @click="goToRoute('OrdersHistory')"
                >
                    <q-icon name="chevron_left" size="20px" class="q-mr-sm" />
                    <span>{{ $t('general.all_orders') }}</span>
                </q-btn>
            </div>
            <!-- order id -->
            <div class="col-auto">
                <span class="text-h6">{{
                    `${$t('general.order_id')}: ${order.id}`
                }}</span>
            </div>
        </div>
        <!-- separator -->
        <q-separator class="q-my-md"></q-separator>
        <!-- order details wrap -->
        <div class="order-details row q-col-gutter-x-lg">
            <!-- order status -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.status') }}
                </div>
                <p v-if="order.orderStatus">
                    {{ JSON.parse(order.orderStatus.title)[locale.key] }}
                </p>
            </div>
            <!-- order type -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.order_type') }}
                </div>
                <p v-if="order.orderType">
                    {{ JSON.parse(order.orderType.description)[locale.key] }}
                </p>
            </div>
            <!-- order date -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.order_date') }}
                </div>
                <p>{{ selectedOrderDate }}</p>
            </div>
            <!-- order payment date -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.payment_date') }}
                </div>
                <p>{{ selectedPaymentDate }}</p>
            </div>
            <!-- order shipped date -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.shipped_date') }}
                </div>
                <p>{{ selectedShippedDate }}</p>
            </div>
            <!-- order global discount -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.discount') }}
                </div>
                <p>{{ selectedDiscount }}</p>
            </div>
            <!-- order total -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.total') }}
                </div>
                <p v-if="order.currencies">
                    {{ JSON.parse(order.total)[order.currencies.key] }}
                </p>
            </div>
            <!-- order currency -->
            <div class="col-4 q-pb-lg">
                <div class="text-caption text-grey-7">
                    {{ $t('general.currency') }}
                </div>
                <p v-if="order.currencies">
                    <span class="text-capitalize"
                        >{{ order.currencies.name }}
                        <span class="text-uppercase"
                            >({{ order.currencies.key }})</span
                        ></span
                    >
                </p>
            </div>
        </div>
        <!-- order items table -->
        <ItemsTable
            :columns="orderItemsColumns"
            :data="orderItems"
            :pagination="orderItemsPagination"
            :currency="order.currencies"
            :currency-options="currencyOptions"
            :order="order"
            :loading="isFetchingOrder"
            class="q-mb-md"
            @request="onRequest"
        >
            <template v-slot:top>
                <div class="col-12">
                    <div class="page-title row items-baseline">
                        <div class="col-auto">
                            <h5>
                                {{ $t('general.order_items') }}
                            </h5>
                        </div>
                    </div>
                </div>
            </template>
        </ItemsTable>
        <!-- invoices table -->
        <InvoicesTable
            :columns="invoiceColumns"
            :data="invoices"
            :order="order"
            :loading="isFetchingInvoices"
            :title="$tc('general.invoices', 2)"
            :pagination="invoicesPagination"
            :filter="invoicesFilter"
            class="q-mb-md"
            @request="onRequestInvoices"
            @new-payment="newPayment"
        >
            <template v-slot:top>
                <div class="col-12">
                    <div class="page-title row items-baseline">
                        <div class="col-auto">
                            <h5>
                                {{ $tc('general.invoices', 2) }}
                            </h5>
                        </div>
                    </div>
                </div>
            </template>
        </InvoicesTable>
        <!-- bottom row -->
        <div class="row q-col-gutter-x-lg q-mt-lg">
            <!-- payment document number -->
            <div class="col-4 q-pb-lg">
                <q-input
                    v-model="selectedDocumentNumber"
                    :label="$t('general.payment_doc_number')"
                    outlined
                    readonly
                />
            </div>
            <!-- upload document button -->
            <div class="col-4 q-pb-lg row">
                <q-btn
                    block
                    color="secondary"
                    style="width: 100%"
                    @click="toggleUploadDocument"
                >
                    <div class="no-wrap">
                        <q-icon name="upload" class="q-mr-sm"></q-icon
                        >{{ $t('actions.upload_proof_of_payment') }}
                    </div>
                </q-btn>
            </div>
        </div>
        <!-- documents list -->
        <div v-if="!emptyOrder" class="row q-col-gutter-x-lg q-mt-lg q-px-md">
            <div class="col-12 q-pb-sm q-pl-lg row text-caption text-grey-7">
                {{ $t('general.documents_attached') }}
            </div>
            <div v-if="!documentsNotInvoices.length">
                {{ $t('notify.no_documents_attached') }}
            </div>
            <div v-else>
                <div
                    v-for="(document, index) in documentsNotInvoices"
                    :key="index"
                    class="col-12 row items-center"
                >
                    <q-icon
                        name="attach_file"
                        size="16px"
                        class="q-mr-xs"
                    ></q-icon
                    >{{ document.name }} -
                    {{ formatDate(document.created_at) }}
                </div>
            </div>
        </div>
        <!-- upload document dialog -->
        <UploadDocument
            :show="uploadDocument.show"
            :document-type="paymentProofType"
            @toggle-dialog="toggleUploadDocument"
            @fetch-order="fetchOrder"
        />
    </div>
</template>

<script>
import PageLoading from '@/components/PageLoading'
import ItemsTable from '@/components/orders/ItemsTable'
import InvoicesTable from '@/components/orders/InvoicesTable'
import UploadDocument from '@/components/orders/UploadDocument'
import translate from '@/local-shared/mixins/translate'
import notify from '@/local-shared/mixins/notify'
import ordersService from '@/services/modules/orders'
import currenciesService from '@/services/modules/currencies'
import documentsTypesService from '@/services/modules/documentstypes'
//import documentsService from '@/services/modules/documents'
import invoicesService from '@/services/modules/invoices'
import formatDate from '@/helpers/date-time'
import store from '@/store'
import _ from 'lodash'

const loadingKeys = {
    fetchingOrder: 'fetching-orders-history',
    fetchingStatus: 'fetching-status',
    fetchingCurrencies: 'fetching-currencies',
    fetchingDocumentsTypes: 'fetching-documents-types',
    fetchingDocuments: 'fetching-documents',
    fetchingInvoices: 'fetching-invoices',
}

export default {
    name: 'SingleOrder',

    components: {
        PageLoading,
        ItemsTable,
        UploadDocument,
        InvoicesTable,
    },

    mixins: [translate, notify],

    data() {
        return {
            order: {},

            currencyOptions: [],

            orderItems: [],
            orderItemsPagination: {
                sortBy: 'id',
                descending: false,
                page: 1,
                rowsPerPage: 99999,
                rowsNumber: 0,
            },

            uploadDocument: {
                show: false,
            },

            documentsTypes: [],

            invoices: [],
            invoicesPagination: {
                sortBy: 'created_at',
                descending: true,
                page: 1,
                rowsPerPage: 20,
                rowsNumber: 0,
            },
            invoicesFilter: {
                orderId: parseInt(this.$route.params.orderId),
            },
        }
    },

    computed: {
        documentsNotInvoices() {
            return this.order?.documents
                ? this.order?.documents.filter(
                      (document) => document.documentType.slug !== 'invoice'
                  )
                : []
        },

        invoiceType() {
            return this.documentsTypes.length
                ? this.documentsTypes.filter(
                      (documentType) => documentType.slug === 'invoice'
                  )
                : []
        },

        paymentProofType() {
            return this.documentsTypes.length
                ? this.documentsTypes.filter(
                      (documentType) => documentType.slug === 'payment_proof'
                  )
                : []
        },

        emptyOrder() {
            return _.isEmpty(this.order)
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        currency() {
            return store.getters['currency/currentCurrency']
        },

        currencyList() {
            return store.getters['currency/currencyList']
        },

        user() {
            return store.getters['session/user']
        },

        isLoading() {
            return (
                this.isFetchingOrder ||
                this.isFetchingStatus ||
                this.isFetchingCurrencies
            )
        },

        isFetchingOrder() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingOrdersHistory
            ).length
        },

        isFetchingCurrencies() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingCurrencies
            ).length
        },

        isFetchingInvoices() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingInvoices
            ).length
        },

        selectedOrderDate() {
            return this.order.order_date
                ? formatDate.fromDatePickerToUxDate(this.order.order_date)
                : '--'
        },

        selectedPaymentDate() {
            return this.order.payment_date
                ? formatDate.fromDatePickerToUxDate(this.order.payment_date)
                : '--'
        },

        selectedShippedDate() {
            return this.order.shipped_date
                ? formatDate.fromDatePickerToUxDate(this.order.shipped_date)
                : '--'
        },

        selectedDiscount() {
            return this.order.discount ? this.order.discount : '--'
        },

        selectedDocumentNumber() {
            return this.order.payment_doc_number
                ? this.order.payment_doc_number
                : '--'
        },

        selectedCurrency: {
            get() {
                const rs = this.currencyOptions.filter(
                    (row) => row.id === this.order.currency_id
                )
                return rs.length === 1 ? rs[0] : rs
            },
            set(v) {
                this.order.currency_id = v.id
            },
        },

        orderItemsColumns() {
            return [
                {
                    name: 'expand_btn',
                    align: 'left',
                },
                // {
                //     name: 'id',
                //     label: 'ID',
                //     field: 'id',
                //     align: 'left',
                //     sortable: false,
                // },
                {
                    name: 'title',
                    label: this.$tc('general.items', 1),
                    field: 'title',
                    align: 'left',
                    sortable: false,
                },
                // {
                //     name: 'preferred_class_id',
                //     label: this.$t('general.preferred_class'),
                //     field: 'preferred_class_id',
                //     align: 'left',
                //     sortable: false,
                // },
                {
                    name: 'price',
                    label: this.$t('general.price'),
                    field: 'price',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'discount',
                    label: `${this.$t('general.discount')} (%)`,
                    field: 'discount',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'quantity',
                    label: this.$t('general.quantity'),
                    field: 'quantity',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'vat',
                    label: this.$t('general.vat'),
                    field: 'tax',
                    align: 'right',
                    sortable: false,
                },
                {
                    name: 'total',
                    label: `${this.$t('general.total')}`,
                    field: 'total',
                    align: 'right',
                    sortable: false,
                },
            ]
        },

        invoiceColumns() {
            return [
                {
                    name: 'expand_btn',
                    label: this.$tc('general.payments', 2),
                    align: 'left',
                    style: 'max-width: 20px',
                },
                {
                    name: 'invoice_number',
                    label: this.$t('invoices.invoice_number'),
                    field: 'invoice_number',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'invoice_date',
                    label: this.$t('invoices.invoice_date'),
                    field: 'invoice_date',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'is_paid',
                    label: this.$t('invoices.payment_status'),
                    field: 'is_paid',
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'items',
                    label: this.$t('invoices.items'),
                    field: 'items',
                    align: 'center',
                    sortable: false,
                },
                {
                    name: 'actions',
                    label: this.$t('general.actions'),
                    field: 'actions',
                    align: 'right',
                },
            ]
        },
    },

    async created() {
        this.fetchDocumentsTypes()
        this.fetchCurrencies()
        this.fetchInvoices()
        await this.fetchOrder()
    },

    methods: {
        goToRoute(routeName) {
            this.$router.push({ name: routeName })
        },

        getSymbol(key) {
            let currency = this.currencyList.find((item) => item.key === key)
            return JSON.parse(currency.definition)['symbol']
        },

        mapColumn(value) {
            if (value == null) {
                return this.invoicesPagination.sortBy
            }

            return value
        },

        mapOrder(value) {
            return value == true ? 'desc' : 'asc'
        },

        onRequestInvoices(paginationAux, filterAux) {
            this.invoicesFilter = filterAux
            this.invoicesPagination = paginationAux
            this.fetchInvoices()
        },

        async fetchOrder() {
            this.loadingAdd(loadingKeys.fetchingOrder)

            let parameters = {
                filter: {
                    id: this.$route.params.orderId,
                    user: this.user.id,
                    publicDocuments: true,
                    withoutContent: true,
                },
                relations: {
                    orderItems: true,
                    currencies: true,
                    paymentMethod: true,
                    documents: true,
                    orderServices: true,
                    orderType: true,
                    orderStatus: true,
                },
            }

            let response = await ordersService.retrieve(parameters)

            if (response.status === 200 && response.data.results.length === 1) {
                this.order = response.data.results[0]
                await this.mapOrderItems()
            } else {
                this.notifyError(response.data.message)
            }

            this.loadingRemove(loadingKeys.fetchingOrder)
        },

        async fetchCurrencies() {
            this.loadingAdd(loadingKeys.fetchingCurrencies)

            let parameters = {
                filter: {},
            }

            let response = await currenciesService.retrieve(parameters)

            if (response.status === 200) {
                this.currencyOptions = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingCurrencies)
        },

        onRequest(paginationAux) {
            this.pagination = paginationAux
        },

        async mapOrderItems() {
            let orderItems = []
            // map course items
            this.order.orderItems.forEach((orderItem) => {
                orderItems.push({
                    id: orderItem.id,
                    price: parseFloat(
                        JSON.parse(orderItem.unit_price)[
                            this.order.currencies.key
                        ]
                    ),
                    course_id: orderItem.course_id,
                    discount: orderItem.discount,
                    invalid: false,
                    item_total: orderItem.item_total,
                    quantity: orderItem.quantity,
                    tax: orderItem.tax,
                    title: orderItem.courses.title,
                    total: parseFloat(
                        JSON.parse(orderItem.item_total)[
                            this.order.currencies.key
                        ]
                    ),
                    unit_price: orderItem.unit_price,
                    enrollments: orderItem.enrollments,
                    preferred_class_id: orderItem.preferred_class_id,
                    preferredClass: orderItem.preferredClass,
                    item_type: 'course',
                })
            })
            // map service items
            this.order.orderServices.forEach((serviceItem) => {
                orderItems.push({
                    id: serviceItem.id,
                    price: parseFloat(
                        JSON.parse(serviceItem.unit_price)[
                            this.order.currencies.key
                        ]
                    ),
                    service_id: serviceItem.service_id,
                    discount: serviceItem.discount,
                    invalid: false,
                    item_total: serviceItem.item_total,
                    quantity: serviceItem.quantity,
                    tax: serviceItem.tax,
                    title: serviceItem.services.name,
                    total: parseFloat(
                        JSON.parse(serviceItem.item_total)[
                            this.order.currencies.key
                        ]
                    ),
                    unit_price: serviceItem.unit_price,
                    item_type: 'service',
                })
            })
            // assign to data orderItems local array variable
            this.orderItems = orderItems
        },

        toggleUploadDocument() {
            this.uploadDocument.show = !this.uploadDocument.show
        },

        selectDocument(url, id) {
            console.log('URL:', url)
            console.log('ID:', id)
        },

        formatDate(value) {
            return value ? formatDate.fromServerToUxDate(value) : null
        },

        async fetchDocumentsTypes() {
            this.loadingAdd(loadingKeys.fetchingDocumentsTypes)

            let parameters = {}

            let response = await documentsTypesService.retrieve(parameters)

            if (response.status === 200) {
                this.documentsTypes = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingDocumentsTypes)
        },

        async fetchInvoices() {
            this.loadingAdd(loadingKeys.fetchingInvoices)

            let parameters = {
                filter: this.invoicesFilter,

                pagination: {
                    perPage: this.invoicesPagination.rowsPerPage,
                    currentPage: this.invoicesPagination.page,
                },

                orderBy: [
                    {
                        column: this.mapColumn(this.invoicesPagination.sortBy),
                        direction: this.mapOrder(
                            this.invoicesPagination.descending
                        ),
                    },
                ],

                relations: {
                    currency: true,
                    payments: true,
                    documents: {
                        withoutContent: true,
                        orderItems: true,
                        orderServices: true,
                    },
                },
            }

            let response = await invoicesService.retrieve(parameters)

            if (response.status === 200) {
                this.invoices = response.data.results
            } else {
                this.notifyError(response)
            }

            this.loadingRemove(loadingKeys.fetchingInvoices)
        },

        newPayment(invoice) {
            this.$router.push({
                name: 'Payment',
                params: {
                    orderId: invoice.order_id,
                    invoiceId: invoice.id,
                },
            })
        },
    },
}
</script>
